<template>
  <div
    v-if="isReady"
    ref="catalogRef"
    :class="{
      catalog: true,
      'catalog--no-padding': config.context === contextType.COLLECTION_PRODUCTS || config.context === contextType.BRAND,
    }"
  >
    <AisInstantSearch
      :search-client="searchClient"
      :index-name="indexName"
      :routing="routing"
      :middlewares="middlewares"
      :future="{
        preserveSharedStateOnUnmount: true,
      }"
    >
      <AisConfigure
        :hits-per-page.camel="$isEnabled('RET-3533') ? 100 : 60"
        :query-languages.camel="[lang]"
        :filters="filters"
        :restrict-searchable-attributes.camel="searchableAttribute"
        :attributes-to-retrieve.camel="attributesToRetrieve"
        :analytics-tags.camel="[`lang_${lang}`, `country_${userCountry}`]"
        :max-values-per-facet.camel="100"
      />
      <AisSearchBox
        class="ds-hidden"
        placeholder="string"
        submit-title="string"
        reset-title="string"
      />
      <template v-for="facet in config.Facets">
        <template v-if="'facets' in facet">
          <CatalogHiddenRefinement
            v-for="groupedFacet in facet.facets"
            :key="groupedFacet.uid"
            :facet="groupedFacet"
          />
        </template>
        <CatalogHiddenRefinement
          v-else-if="facet.name !== productTypeSlug"
          :key="facet.uid"
          :facet="facet"
        />
      </template>
      <slot
        v-if="config.withBreadCrumb"
        name="breadcrumb"
        :catalog-state="aisState"
      >
        <CatalogBreadcrumb
          v-if="categories"
          :search-query="aisState.searchQuery.value"
          :categories="categories"
        />
      </slot>
      <div class="catalog__top-header">
        <slot
          name="topHeader"
          :catalog-state="aisState"
        />
      </div>
      <div
        :class="{
          catalog__container: true,
          'catalog__container--with-filter-controls': filtersShown,
        }"
      >
        <slot
          name="sidebar"
          :catalog-state="aisState"
        >
          <div
            :class="{
              'catalog__sidebar-container': true,
              'catalog__sidebar-container--expanded': filtersShown,
              'catalog__sidebar-container--sticky': canToggleFilters,
            }"
          >
            <FilterSidebar
              v-if="config.withFilterSidebar"
              ref="filterSidebarRef"
              :class="{
                catalog__sidebar: true,
                'catalog__sidebar--sticky': canToggleFilters,
              }"
              :facets="sidebarFacets"
              :label-overrides="labelOverrides"
              :show-categories="Boolean(config.withCategoriesFilter && categories)"
              :show-view-filter="Boolean(config.withTabs)"
              :current-view="currentView"
              hide-modal-button
              @info-cta-clicked="trackFilterInfoCtaClicked"
              @discount-filter-opened="trackFilterPanelOpened"
              @view-changed="(view) => emit('update:currentView', view)"
            >
              <template #savedSearch>
                <div v-if="isEnabled('RET-3236') && config.context === contextType.SEARCH">
                  <div class="ds-flex ds-mt-4 lg:ds-mt-0">
                    <SavedSearch :query-id="lastQueryID" />
                  </div>
                </div>
              </template>
            </FilterSidebar>
          </div>
        </slot>
        <main class="catalog__main">
          <slot
            v-if="config.withTitle"
            name="header"
            :catalog-state="aisState"
            :track-brand-suggestion-clicked="trackBrandSuggestionClicked"
          />
          <div class="catalog__sticky-bar">
            <ProductTypeShortcuts
              v-if="productTypeAttribute"
              :attribute="productTypeAttribute"
              :name-by-id="productTypeNameById"
              class="catalog__product-type-shortcuts"
            />
            <div
              v-if="config.withFilterSidebar || config.withSort"
              class="catalog__sort-mobile"
            >
              <AkTooltipInformation
                :is-enabled="shouldHighlightDiscountMobile"
                :show="shouldHighlightDiscountMobile"
                trigger="manual"
                close-button
                type="invert"
                placement="bottom"
                custom-class="discount-highlight"
                :title="$t('discovery.catalog.filters.discountHighlight.mobileTitle')"
                :content="$t('discovery.catalog.filters.discountHighlight.mobileContent')"
                @close="dismissDiscountHighlight"
                @apply-show="startWatchingMobileHighlightSize"
                @hide="stopWatchingMobileHighlightSize"
              >
                <AkHighlight
                  :enable="shouldHighlightDiscountMobile"
                  rounded="md"
                  @click="dismissDiscountHighlight"
                >
                  <div
                    ref="mobileHighlightWrapperRef"
                    class="ds-flex ds-items-center ds-gap-3"
                  >
                    <AisCurrentRefinements
                      v-if="config.withFilterSidebar"
                      :excluded-attributes="['catalog_hierarchical_categories.v3.lvl0', 'query']"
                    >
                      <template #default="{ items }">
                        <AkButton
                          color="white"
                          :outlined="true"
                          @click="filterSidebarRef?.openModal()"
                        >
                          {{ $t('All filters') }}
                          <AkPill
                            class="ds-ml-2 !ds-bg-success-700"
                            :text-color="AkPillColor.AccentAlt"
                            active
                            :content="getFiltersNumber(items)"
                          />
                        </AkButton>
                      </template>
                    </AisCurrentRefinements>
                    <SortsBy
                      v-if="config.withSort"
                      v-model:sort-by="sortBy"
                      :current-sort="sortBy"
                      :index-name="indexName"
                      :context="config.context"
                    />
                  </div>
                </AkHighlight>
              </AkTooltipInformation>
            </div>
            <div class="catalog__top-bar">
              <AisCurrentRefinements
                v-if="canToggleFilters"
                :excluded-attributes="['catalog_hierarchical_categories.v3.lvl0', 'query']"
              >
                <template #default="{ items }">
                  <span class="ds-flex ds-items-center ds-gap-2">
                    <AkButton
                      link
                      size="lg"
                      :symbol="filtersShown ? 'chevron-left' : 'chevron-right'"
                      class="catalog__top-bar-toggle"
                      @click="toggleWithFilters"
                    >
                      {{ filtersShown ? $t('discovery.catalog.filters.hide') : $t('discovery.catalog.filters.show') }}
                    </AkButton>
                    <AkPill
                      v-if="!filtersShown && getFiltersNumber(items) > 0"
                      class="!ds-bg-success-700"
                      :text-color="AkPillColor.AccentAlt"
                      active
                      :content="getFiltersNumber(items)"
                    />
                  </span>
                </template>
              </AisCurrentRefinements>
              <span
                v-if="canToggleFilters"
                class="catalog__top-bar-separator"
                aria-hidden="true"
              />
              <span
                v-if="config.withQuickFilters"
                class="catalog__top-bar-label"
              >
                {{ $t('discovery.catalog.mostPopular') }}
              </span>
              <QuickFiltersBar
                v-if="config.withQuickFilters"
                :facets="config.quickFilters"
                :label-overrides="labelOverrides"
                :highlight-discount="shouldHighlightDiscountDesktop"
                @discount-filter-interacted="dismissDiscountHighlight"
                @discount-filter-opened="trackFilterPanelOpened"
              />
              <div
                v-if="config.withSort"
                class="ds-ml-auto ds-flex-shrink-0"
              >
                <AkTooltipInformation
                  :is-enabled="shouldHighlightDiscountDesktop"
                  :show="shouldHighlightDiscountDesktop"
                  trigger="manual"
                  close-button
                  type="invert"
                  placement="bottom-end"
                  custom-class="discount-highlight"
                  :title="$t('discovery.catalog.filters.discountHighlight.sortTitle')"
                  :content="$t('discovery.catalog.filters.discountHighlight.sortContent')"
                  @close="dismissDiscountHighlight"
                  @apply-show="startTrackingHighlightScroll"
                  @hide="stopTrackingHighlightScroll"
                >
                  <AkHighlight
                    :enable="shouldHighlightDiscountDesktop"
                    rounded="md"
                    @click="dismissDiscountHighlight"
                  >
                    <SortsBy
                      v-model:sort-by="sortBy"
                      :current-sort="sortBy"
                      link
                      :index-name="indexName"
                      :context="config.context"
                    />
                  </AkHighlight>
                </AkTooltipInformation>
              </div>
            </div>
            <CatalogSearchInput
              v-if="config.withSearchInput"
              class="catalog__search-input"
            />
          </div>
          <div class="ds-flex ds-flex-col ds-gap-5">
            <div
              v-if="config.withSort"
              class="catalog__head-side-bar"
            />
            <slot
              name="hits-header"
              :catalog-state="aisState"
            />
            <CatalogHits
              :config="config"
              :related-collection-insertions="relatedCollectionInsertions"
              :compact="filtersShown"
              :filters-shown="filtersShown"
            >
              <template #product="productProps">
                <slot
                  name="product"
                  v-bind="productProps"
                />
              </template>
              <template #noResults>
                <AisClearRefinements>
                  <template #default="{ refine }">
                    <SearchNoResult
                      class="ds-my-4"
                      data-testid="search-results-none"
                      :search-term="aisState.searchQuery.value"
                      :context="config.context"
                      :has-refinements="hasRefinements"
                      @click="refine"
                    />
                  </template>
                </AisClearRefinements>
              </template>
            </CatalogHits>
            <CatalogPagination
              :total-pages="config.maxPages"
              @page-change="handlePageChange"
            />
            <slot
              v-if="shouldDisplayFooter"
              name="hits-footer"
              :catalog-state="aisState"
            />
          </div>
        </main>
      </div>
      <footer class="catalog__footer">
        <slot
          v-if="shouldDisplayFooter"
          name="footer"
          :catalog-state="aisState"
        />
      </footer>
      <PortalTarget name="modal-target" />
    </AisInstantSearch>
  </div>

  <!-- `isReady` waits on the regions fetch, so the catalog renders nothing for a few hundred ms.
       Callers that would otherwise show a blank gap can fill this slot.
       Gated on the slot being passed, and wrapped in an element rather than a <template>: a
       fragment root is neither animatable inside <Transition> nor able to inherit fallthrough
       attributes, so an ungated version regresses every caller that ignores this slot. -->
  <div v-else-if="$slots.loading">
    <slot name="loading" />
  </div>
</template>

<script lang="ts">
import { PropType, toRefs, ref, computed, defineComponent, onBeforeMount, onBeforeUnmount, watch, watchEffect, nextTick } from 'vue';
import {
  Config,
  Context,
  CatalogGridInsertion,
  HistoryRouterManager,
  HistoryRouterProduct,
  HistoryRouterBrand,
  getAttributeToRetrieve,
  getDefaultFilter,
  getRestrictedSearchableAttributes,
  resolveSortBy,
  getLocations,
  type Location,
} from '@bc/discovery/domain/catalog';
import { CatalogSearchApiClient } from '../services/catalog-search-api-client';
import { InstantSearchRouterStateMapper } from '../services/instant-search-state-mapper';
import CatalogPagination from '@monolith/legacy/components/catalog-pagination.vue';
import CatalogBreadcrumb from './catalog-breadcrumb.vue';
import CatalogSearchInput from './catalog-search-input.vue';
import CatalogHits from './catalog-hits.vue';
import { DiscoveryTrackingEvents } from '@bc/discovery/domain/tracking';
import StoreRootHelpers from '@monolith/legacy/store/helpers';
import { useRoute, useRouter } from 'vue-router';
import useGlobals from '@core/composables/use-globals';
import { useCategories } from '@bc/discovery/domain/catalog';
import type { ProductSortBy } from '@bc/discovery/domain/catalog/types/product-search-parameters';
import FilterSidebar from '../components/filter-sidebar/filter-sidebar.vue';
import QuickFiltersBar from '../components/quick-filters-bar.vue';
import SortsBy from '../components/sorts-by/sorts-by.vue';
import SearchNoResult from '../components/search-no-result/search-no-result.vue';
import { CatalogHiddenRefinement } from '@bc/discovery/ui/catalog';
import { useViewPort } from '@core/composables/use-viewport';
import { PortalTarget } from 'portal-vue';
import { getCatalogTrackingHandlers, isDiscountFacetTrackingName } from '../services/tracking';
import useCatalogTrackingData from '../composables/use-catalog-tracking-data';
import { useEventBus } from '@bc/shared';
import { useLabels } from '../composables/use-labels';
import { useAisState } from '../composables/use-ais-state';
import { useCatalogTilesPerRow } from '../composables/use-catalog-tiles-per-row';
import { captureRetailerCoreDiscoveryException } from '@core/plugins/sentry/helper';
import { AisSearchBox, AisClearRefinements, AisCurrentRefinements } from 'vue-instantsearch/vue3/es';
import { debounce } from '@core/utilities/lodash-utils';
import SavedSearch from '../components/saved-search/saved-search.vue';
import { FeatureFlag, isEnabled } from '@client/monolith/legacy/services/features';
import { AkButton, AkPill, AkPillColor, AkHighlight, AkTooltipInformation } from '@ankorstore/design-system';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import ProductTypeShortcuts from '../components/product-type-shortcuts/product-type-shortcuts.vue';
import { useHighlightDisplay } from '@core/composables/use-highlight-display';
import { useHighlightScrollTracking } from '@core/composables/use-highlight-scroll-tracking';

const DISCOUNT_HIGHLIGHT_FEATURE_NAME = 'discount-filter-sort-highlight';

export default defineComponent({
  name: 'DiscoveryCatalog',
  components: {
    AkButton,
    AkPill,
    AkHighlight,
    AkTooltipInformation,
    AisSearchBox,
    AisClearRefinements,
    AisCurrentRefinements,
    CatalogPagination,
    CatalogBreadcrumb,
    CatalogSearchInput,
    CatalogHits,
    CatalogHiddenRefinement,
    FilterSidebar,
    QuickFiltersBar,
    PortalTarget,
    SortsBy,
    SearchNoResult,
    SavedSearch,
    ProductTypeShortcuts,
  },
  props: {
    indexName: {
      type: String,
      required: true,
    },
    config: {
      type: Object as PropType<Config>,
      required: true,
    },
    brandId: {
      type: Number,
      default: null,
    },
    customFilter: {
      type: String,
      default: null,
    },
    valuePropositionOverride: {
      type: String,
      default: null,
    },
    basePath: {
      type: String,
      default: '',
    },
    // Non-catalog query params to keep when the catalog rewrites the URL from its own state
    // (utm_* are always kept). E.g. the lift page passes ['prices', 'invite']. Defaults to
    // undefined (not []) so the prop isn't rendered for consumers that don't set it.
    preserveParams: {
      type: Array as PropType<string[]>,
      default: undefined,
    },
    currentView: {
      type: String,
      required: false,
      default: undefined,
    },
    relatedCollectionInsertions: {
      type: Object as PropType<CatalogGridInsertion>,
      default: undefined,
    },
    category: {
      type: Number,
      default: null,
    },
    categoryLevel: {
      type: Number,
      default: undefined,
    },
    categoryIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    queryOverride: {
      type: String,
      default: undefined,
    },
    withFilters: {
      type: Boolean,
      default: true,
    },
    canToggleFilters: {
      type: Boolean,
      default: isEnabled('RET-3533'),
    },
  },
  emits: ['update:trackingSectionId', 'update:currentView', 'update:catalogState', 'update:filtersShown'],
  setup(props, { emit }) {
    const { indexName, config, brandId, valuePropositionOverride, category, categoryLevel, categoryIds, withFilters } = toRefs(props);
    const { dimensions } = useViewPort();
    const { aisState, aisStateMiddleware } = useAisState();
    const hasRefinements = computed(() => Boolean(aisState.refinements.value.refinementCount));
    const catalogRef = ref<HTMLDivElement>(undefined);
    const contextType = Context;
    const filters = ref();
    const { lang } = useGlobals();
    const router = useRouter();
    const route = useRoute();

    // Declared here (rather than closer to where it's read further down) so it's available to
    // getCatalogTrackingHandlers below, which needs its live value to enrich product-click
    // tracking with whether a discount sort is currently active.
    const sortBy = ref<ProductSortBy | undefined>();
    watchEffect(() => {
      const hashParams = new URLSearchParams(router.currentRoute.value.hash.substring(1));
      // Resolved against the surface's own option set, so a sort this catalog does not offer (a stale
      // bookmark, a link shared from another catalog, or one the kill switch just withdrew) is dropped
      // from the search instead of silently reordering it with something the retailer cannot undo.
      sortBy.value = resolveSortBy(config.value.context, hashParams.get('sortBy') ?? undefined);
    });
    const { user, userCountry, userCurrency, isStaff } = StoreRootHelpers.useGetters([
      'user',
      'userCountry',
      'userCurrency',
      'isStaff',
    ]);
    const { categories } = useCategories();
    const { isAboveOrEqualXl, isLg, isAboveOrEqualLg } = useBreakpoints();

    const { eventBus } = useEventBus();
    const catalogTrackingData = useCatalogTrackingData({
      config,
      valueProposition: computed(() => valuePropositionOverride?.value ?? aisState.searchQuery.value),
    });
    const {
      filtersTracker,
      trackSectionCreated,
      trackTileProductClicked,
      trackTileBrandClicked,
      trackTileSponsoredProductClicked,
      trackProductSnippetBrandClicked,
      trackQuickAddToCartProductAdded,
      trackQuickAddToCartClicked,
      // TODO: this method should be moved to a search shell tracking module
      // for the time being, i'll pass it using a slot prop.
      trackBrandSuggestionClicked,
      trackRelatedCollectionClicked,
      trackFilterInfoCtaClicked,
      trackFilterPanelOpened,
      trackSortApplied,
    } = getCatalogTrackingHandlers(catalogTrackingData, { refinements: aisState.refinements, sortBy });

    const locations = ref<Location[]>();

    const filtersShown = ref(withFilters.value);
    const toggleWithFilters = () => (filtersShown.value = !filtersShown.value);

    useCatalogTilesPerRow(filtersShown);
    const sponsoredTilesPerRow = computed(() => (isAboveOrEqualXl.value ? 5 : isLg.value ? 4 : 2));
    // Do not use the actual number of tiles per row,
    // it will trigger a search when the user toggle the filter sidebar
    const searchClient = new CatalogSearchApiClient(props.config, sponsoredTilesPerRow, userCountry.value);

    const lastQueryID = ref();
    searchClient.onResponse((response) => {
      if ('id' in response.results[0]) {
        lastQueryID.value = response.results[0]?.id;
      }
      return response;
    });

    const attributesToRetrieve = computed(() => getAttributeToRetrieve(config.value.template));
    const searchableAttribute = computed(() => getRestrictedSearchableAttributes(config.value.context));
    const PRODUCT_TYPE_PARENT_CATEGORY_IDS = [1262];
    const productTypeAttribute = computed(() => {
      if (!isEnabled('DAT-3167')) { return null; }
      if (config.value?.context !== Context.CATEGORY_PRODUCTS) { return null; }
      if (categoryLevel.value !== 2) { return null; }
      const isAllowed = PRODUCT_TYPE_PARENT_CATEGORY_IDS.some((parentId) => categoryIds.value?.includes(parentId));
      if (!isAllowed) { return null; }
      return 'product_type.id';
    });

    const productTypeSlug = 'product_type.id';

    const productTypeNameById = computed<Record<string, string>>(() => {
      const ptFacet = config.value?.Facets?.find((f) => 'name' in f && f.name === productTypeSlug);
      if (!ptFacet || !('choices' in ptFacet) || !ptFacet.choices?.length) {return {};}
      return Object.fromEntries((ptFacet.choices as Array<{ slug: string; name: string }>).map((c) => [c.slug, c.name]));
    });

    const sidebarFacets = computed(() =>
      productTypeAttribute.value !== null
        ? config.value.Facets
        : config.value.Facets.filter((f) => f.name !== productTypeSlug)
    );

    const clearProductTypeFilter = () => {
      const fullPath = router.currentRoute.value.fullPath;
      const hashIndex = fullPath.indexOf('#');
      const pathWithQuery = hashIndex >= 0 ? fullPath.slice(0, hashIndex) : fullPath;
      const hashStr = hashIndex >= 0 ? fullPath.slice(hashIndex + 1) : '';
      const hashParams = new URLSearchParams(hashStr);
      if (!hashParams.has('product-type')) { return; }
      hashParams.delete('product-type');
      const newHash = hashParams.toString();
      router.push(pathWithQuery + (newHash ? '#' + newHash : ''));
    };

    watch(category, (newCat, oldCat) => {
      if (!oldCat || newCat === oldCat) { return; }
      clearProductTypeFilter();
    });

    watch(() => route.query.categories, (newCats, oldCats) => {
      if (oldCats === undefined || newCats === oldCats) { return; }
      clearProductTypeFilter();
    });

    const historyRouterParams = {
      router,
      getCategories: () => categories.value,
      getLocations: () => locations.value,
      getLang: () => lang,
      getCurrency: () => userCurrency.value as string,
      urlManager: () => config.value.urlManager,
      getBasePath: () => props.basePath,
      preserveParams: props.preserveParams,
    };
    const routing = {
      router: new HistoryRouterManager(
        historyRouterParams,
        computed(() => config.value.template),
        new HistoryRouterProduct(historyRouterParams),
        new HistoryRouterBrand(historyRouterParams)
      ),
      stateMapping: new InstantSearchRouterStateMapper(() => indexName.value, props.queryOverride),
    };

    const isReady = computed(() => Boolean(indexName.value && config.value && categories.value && locations.value));
    const shouldDisplayFooter = computed(() => aisState.status.value === 'idle' && aisState.hits.value?.length > 0);
    const { labelOverrides } = useLabels(config, category, ref(lang));

    watchEffect(() => emit('update:trackingSectionId', catalogTrackingData.trackingSectionId.value));
    watchEffect(() => emit('update:catalogState', aisState));
    watchEffect(() => emit('update:filtersShown', filtersShown.value));

    const isReadyFilterTracking = computed(() => aisState.status.value === 'idle' && route.fullPath);

    watch(
      () => ({ status: aisState.status.value, page: aisState.pagination.value?.page }),
      debounce(({ status, page }) => {
        if (status === 'idle' && route.path && page !== undefined && Array.isArray(aisState.hits.value)) {
          trackSectionCreated(aisState.refinements.value, page, aisState.hits.value);
        }
      }, 500),
      { deep: true }
    );

    watch(
      isReadyFilterTracking,
      debounce(() => {
        filtersTracker(aisState.refinements.value);
      }, 500)
    );

    // No `immediate`, so this only fires on an actual change - not the initial value populated
    // by sortBy's own watchEffect above (matches filtersTracker's own "don't fire on page load"
    // behavior).
    watch(sortBy, (value) => {
      if (value) {
        trackSortApplied(value);
      }
    });

    watchEffect(() => {
      filters.value = getDefaultFilter(
        {
          country: userCountry.value,
          brandId: brandId.value,
          user: user.value,
          categoryId: category.value,
          isStaff: isStaff.value,
          sortBy: sortBy.value,
        },
        config.value.context,
        props.customFilter
      );
    });

    onBeforeMount(async () => {
      try {
        const _locations = await getLocations();
        await nextTick();
        locations.value = _locations;
      } catch (error) {
        captureRetailerCoreDiscoveryException(error);
      }
    });

    eventBus
      .on(DiscoveryTrackingEvents.TILE_PRODUCT_CLICKED, trackTileProductClicked)
      .on(DiscoveryTrackingEvents.TILE_BRAND_CLICKED, trackTileBrandClicked)
      .on(DiscoveryTrackingEvents.TILE_SPONSORED_PRODUCT_CLICKED, trackTileSponsoredProductClicked)
      .on(DiscoveryTrackingEvents.PRODUCT_SNIPPET_BRAND_CLICKED, trackProductSnippetBrandClicked)
      .on(DiscoveryTrackingEvents.QUICK_ADD_TO_CART_CLICKED, trackQuickAddToCartClicked)
      .on(DiscoveryTrackingEvents.QUICK_ADD_TO_CART_PRODUCT_ADDED, trackQuickAddToCartProductAdded)
      .on(DiscoveryTrackingEvents.RELATED_COLLECTION_CLICKED, trackRelatedCollectionClicked);

    const handlePageChange = () => {
      const pageHeaderHeight = (document.getElementsByClassName('ak-header')[0] as HTMLDivElement)?.offsetHeight ?? 0;
      catalogRef.value?.scrollIntoView();
      window.scrollBy(0, -pageHeaderHeight);
    };

    const headerOffset = computed(() => `${dimensions.desktopHeaderRect?.height || dimensions.mobileHeaderRect?.height || 0}px`);
    const maxSidebarHeight = computed(() => `calc(100vh - ${headerOffset.value})`);

    const filterSidebarRef = ref<InstanceType<typeof FilterSidebar>>();

    const getFiltersNumber = (items: { refinements?: unknown[] }[]) =>
      items.reduce((total, item) => total + (item.refinements?.length ?? 0), 0);

    // One-time AK Highlight callout introducing the discount filter/sort (PRICE-8). Shared flag:
    // dismissing or interacting with either trigger hides both, for this retailer.
    //
    // useHighlightDisplay's own `criteria.featureFlag` gate assumes isEnabled(flag) === true
    // means "show" - the opposite of this repo's kill-switch convention (isEnabled === true
    // means "kill it"), so the flag isn't passed there; it's applied as its own condition below.
    const { isStatusValid: shouldHighlightDiscount, setHighlightStatus } = useHighlightDisplay(
      DISCOUNT_HIGHLIGHT_FEATURE_NAME,
      {}
    );
    const dismissDiscountHighlight = () => setHighlightStatus(DISCOUNT_HIGHLIGHT_FEATURE_NAME);
    const isDiscountHighlightKilled = computed(() => isEnabled(FeatureFlag.DisableDiscountFilterSortHighlight));
    // Some contexts (e.g. brand pages) never get a discount facet at all - see
    // CatalogFacetConfigService, which only declares it for SEARCH/CATEGORY_PRODUCTS - so without
    // this check the highlight would advertise a filter/sort that doesn't exist on the page.
    const hasDiscountFacet = computed(() => config.value.quickFilters.some((filter) => isDiscountFacetTrackingName(filter.trackingName)));
    // Each side is CSS-hidden (not unmounted) below/above `lg`; forcing a tooltip open on a
    // hidden, zero-size anchor makes floating-vue fall back to a stray position. Gate by the
    // same breakpoint the CSS uses so only the actually-visible instance opens.
    const shouldHighlightDiscountDesktop = computed(
      () => shouldHighlightDiscount.value && !isDiscountHighlightKilled.value && hasDiscountFacet.value && isAboveOrEqualLg.value
    );
    const shouldHighlightDiscountMobile = computed(
      () => shouldHighlightDiscount.value && !isDiscountHighlightKilled.value && hasDiscountFacet.value && !isAboveOrEqualLg.value
    );

    const { startTrackingScroll: startTrackingHighlightScroll, stopTrackingScroll: stopTrackingHighlightScroll } = useHighlightScrollTracking(
      '.ak-tooltip--discount-highlight.v-popper__popper--shown'
    );

    // The mobile wrapper also contains AisCurrentRefinements, whose "All filters" button only
    // reflects its real content once the search response arrives (latency varies, so no fixed
    // delay works). A ResizeObserver re-nudges whenever that response actually changes the layout.
    const mobileHighlightWrapperRef = ref<HTMLElement>();
    let mobileHighlightResizeObserver: ResizeObserver | undefined;
    const startWatchingMobileHighlightSize = () => {
      startTrackingHighlightScroll();
      if (typeof ResizeObserver === 'undefined' || !mobileHighlightWrapperRef.value || mobileHighlightResizeObserver) {
        return;
      }
      mobileHighlightResizeObserver = new ResizeObserver(() => window.dispatchEvent(new Event('resize')));
      mobileHighlightResizeObserver.observe(mobileHighlightWrapperRef.value);
    };
    const stopWatchingMobileHighlightSize = () => {
      stopTrackingHighlightScroll();
      mobileHighlightResizeObserver?.disconnect();
      mobileHighlightResizeObserver = undefined;
    };
    onBeforeUnmount(stopWatchingMobileHighlightSize);

    return {
      headerOffset,
      maxSidebarHeight,
      filtersShown,
      toggleWithFilters,
      aisState,
      catalogRef,
      isReady,
      emit,
      lang,
      userCountry,
      categories,
      searchClient,
      trackBrandSuggestionClicked,
      trackFilterInfoCtaClicked,
      trackFilterPanelOpened,
      trackSortApplied,
      searchableAttribute,
      attributesToRetrieve,
      filters,
      routing,
      middlewares: [aisStateMiddleware],
      labelOverrides,
      contextType,
      handlePageChange,
      route,
      shouldDisplayFooter,
      sortBy,
      hasRefinements,
      isEnabled,
      lastQueryID,
      productTypeAttribute,
      productTypeSlug,
      productTypeNameById,
      sidebarFacets,
      filterSidebarRef,
      getFiltersNumber,
      AkPillColor,
      shouldHighlightDiscountDesktop,
      shouldHighlightDiscountMobile,
      dismissDiscountHighlight,
      startTrackingHighlightScroll,
      stopTrackingHighlightScroll,
      mobileHighlightWrapperRef,
      startWatchingMobileHighlightSize,
      stopWatchingMobileHighlightSize,
    };
  },
});
</script>

<style scoped lang="scss">
@use '@css/abstracts/design-tokens' as d;
@use '@css/abstracts/mixins' as m;

$sidebar-width: 20.4rem;

.catalog {
  // These styles should be moved to parent
  @include m.container;
  margin: 0 auto 2rem;

  $root: &;

  &__sidebar-container {
    @include d.lg() {
      @apply ds-flex-none ds-w-0 ds-mr-0 ds-overflow-hidden;
      transition: all 0.05s ease-in-out;

      &--sticky {
        @apply ds-sticky ds-overflow-y-auto ds-pt-2;
        top: v-bind(headerOffset);
        max-height: v-bind(maxSidebarHeight);
        margin-top: -0.5rem;
        scrollbar-gutter: stable;
      }

      &--expanded {
        @apply ds-mr-5;
        width: $sidebar-width;
      }
    }
  }

  &__sidebar {
    @include d.lg() {
      width: $sidebar-width;

      &--sticky {
        @apply ds-pr-3;
      }
    }
  }

  &__main {
    @apply ds-flex-1 ds-min-w-0 ds-flex ds-flex-col ds-mt-2 ds-gap-4;
  }

  &__top-header {
    @include d.xl() {
      margin-bottom: 2.5rem;
    }
  }

  &__sticky-bar {
    @apply ds-sticky ds-z-20 ds-bg-white ds-flex ds-flex-col ds-gap-4 ds-py-2;
    top: v-bind(headerOffset);
    margin-top: -0.5rem;
  }

  &__top-bar {
    @apply ds-hidden ds-items-center ds-justify-start ds-gap-3;
    @include d.lg() {
      @apply ds-flex;
    }

    &-toggle {
      @apply ds-whitespace-nowrap ds-flex-shrink-0;
    }

    &-separator {
      @apply ds-flex-shrink-0 ds-self-stretch ds-w-px ds-bg-neutral-300;
    }

    &-label {
      @apply ds-text-sm ds-font-semibold ds-text-neutral-900 ds-whitespace-nowrap ds-flex-shrink-0;
    }
  }

  &__container {
    @apply ds-flex ds-flex-col;
    @include d.lg() {
      @apply ds-flex-row;
    }
  }

  &__category-filter {
    @include d.lg() {
      width: 17rem;
    }
  }

  &__head-side-bar {
    @apply ds-pt-5;
    @include d.md() {
      @apply ds-pt-0;
    }
    @include d.lg() {
      @apply ds-flex;
    }
    .sortByDesktop {
      @apply ds-ml-auto ds-hidden;
      @include d.lg {
        @apply ds-block;
      }
    }
    .sortByMobile {
      @apply ds-ml-auto ds-block;
      @include d.md() {
        @apply ds-hidden;
      }
    }
  }

  &__product-type-shortcuts {
    @apply ds-w-full;
  }

  &__sort-mobile {
    @apply ds-flex ds-items-center ds-gap-3 ds-mt-3;
    @include d.lg() {
      @apply ds-hidden;
    }
  }

  &__search-input {
    @apply ds-flex-1 ds-min-w-[376px];
    width: 18rem;
  }


  &__footer {
    @apply ds-my-8 ds-gap-8;
  }

  &--no-padding {
    @apply ds-p-0;
  }
}
</style>

<style lang="scss">
// AkTooltipInformation only pads .content for closeButton+!title; with both a title and a close
// button (our case) nothing separates the title text from the close icon. Also widens the
// padding/width to match the Figma spec (more generous than the component's default).
.ak-tooltip.ak-tooltip--discount-highlight .v-popper__inner {
  padding: 1rem;
  max-width: 300px;
}

.ak-tooltip.ak-tooltip--discount-highlight .ak-tooltip__title {
  margin-bottom: 0.5rem;
  padding-right: 1.25rem;
}

.ak-tooltip.ak-tooltip--discount-highlight .ak-tooltip__closer {
  top: 0.5rem;
  right: 0.5rem;
}

// Arrow default is 6px (border-width trick); `top` must match it to stay flush against the
// popper (placement here is always "bottom").
.ak-tooltip.ak-tooltip--discount-highlight .v-popper__arrow-outer {
  border-width: 8px;
  top: -8px;
}

// AkTooltipInformation's popper is teleported to <body> and hardcodes z-index: 10000 (see
// AkTooltipInformation.vue), so it renders above EVERYTHING at the root stacking context -
// including the main nav's category mega-menu (effective z-index: 2 via #navBar/.navbar) and,
// on mobile, this tooltip's own anchor sits inside .catalog__sticky-bar (`position: sticky`,
// z-index: 20), which was painting over the tooltip's arrow. 21 clears the sticky bar on both
// breakpoints (verified live); the nav mega-menu case doesn't spatially overlap this tooltip
// for the categories checked so far, but hasn't been exhaustively verified across all of them.
.ak-tooltip.ak-tooltip--discount-highlight.v-popper__popper {
  z-index: 21;
}
</style>
